import type { Block } from '../types';
import { defaultProps, getDefinition } from '../components/catalog';

export function newId(prefix = 'b'): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function makeBlock(type: string, overrides: Partial<Block> = {}): Block {
  const def = getDefinition(type);
  const block: Block = {
    id: newId(),
    type,
    props: { ...defaultProps(type), ...(overrides.props || {}) },
  };
  if (def?.tier === 'container') {
    block.children = overrides.children ?? defaultChildrenFor(type);
  }
  return { ...block, ...overrides, props: block.props };
}

function defaultChildrenFor(type: string): Block[] {
  const def = getDefinition(type);
  if (!def?.defaultChildType) return [];
  if (type === 'question-list') {
    return [makeBlock('question'), makeBlock('question'), makeBlock('question')];
  }
  if (type === 'choice-list') {
    return ['A', 'B', 'C', 'D'].map((l) => makeBlock('choice', { props: { label: l, correct: false } }));
  }
  if (type === 'card-grid') {
    return Array.from({ length: 4 }).map((_, i) => makeBlock('card', { props: {
      tag: `Term · 0${i + 1}`,
      title: ['Mitosis', 'Vector', 'Quotient', 'Métaphore'][i] || 'New term',
      body: '<p>Definition…</p>',
      foot: 'Side A',
    } }));
  }
  if (type === 'rubric') {
    return [
      makeBlock('rubric-row', { props: { criterion: 'Thesis' } }),
      makeBlock('rubric-row', { props: { criterion: 'Evidence' } }),
      makeBlock('rubric-row', { props: { criterion: 'Style' } }),
      makeBlock('rubric-row', { props: { criterion: 'Mechanics' } }),
    ];
  }
  if (type === 'agenda') {
    return [
      makeBlock('agenda-row', { props: { time: '0:00', name: 'Warm-up', desc: 'Bell-ringer prediction' } }),
      makeBlock('agenda-row', { props: { time: '0:08', name: 'Direct teach', desc: 'Mini-lecture & demo' } }),
      makeBlock('agenda-row', { props: { time: '0:22', name: 'Group practice', desc: 'Lab stations' } }),
      makeBlock('agenda-row', { props: { time: '0:40', name: 'Exit ticket', desc: '3-2-1 reflection' } }),
    ];
  }
  return [];
}

/* ---------- Find / mutate ----------- */

export function findBlock(tree: Block[], id: string): Block | null {
  for (const b of tree) {
    if (b.id === id) return b;
    if (b.children) {
      const found = findBlock(b.children, id);
      if (found) return found;
    }
  }
  return null;
}

export function findParent(tree: Block[], id: string, parent: Block | null = null): { parent: Block | null; index: number } | null {
  for (let i = 0; i < tree.length; i += 1) {
    const b = tree[i];
    if (b.id === id) return { parent, index: i };
    if (b.children) {
      const r = findParent(b.children, id, b);
      if (r) return r;
    }
  }
  return null;
}

function mapTree(tree: Block[], fn: (b: Block) => Block): Block[] {
  return tree.map((b) => {
    const next = fn(b);
    if (next.children) {
      return { ...next, children: mapTree(next.children, fn) };
    }
    return next;
  });
}

export function updateBlockProps(tree: Block[], id: string, patch: Record<string, unknown>): Block[] {
  return mapTree(tree, (b) =>
    b.id === id ? { ...b, props: { ...b.props, ...patch } } : b,
  );
}

export function removeBlock(tree: Block[], id: string): Block[] {
  return tree
    .filter((b) => b.id !== id)
    .map((b) => (b.children ? { ...b, children: removeBlock(b.children, id) } : b));
}

export function insertAfter(tree: Block[], targetId: string | null, block: Block): Block[] {
  if (!targetId) return [...tree, block];
  const out: Block[] = [];
  let inserted = false;
  for (const b of tree) {
    out.push(b.children ? { ...b, children: insertAfter(b.children, targetId, block) } : b);
    if (b.id === targetId) {
      out.push(block);
      inserted = true;
    }
  }
  if (!inserted && tree.length === 0) return [block];
  return out;
}

export function appendChild(tree: Block[], parentId: string, child: Block): Block[] {
  return mapTree(tree, (b) => {
    if (b.id !== parentId) return b;
    const children = b.children ? [...b.children, child] : [child];
    return { ...b, children };
  });
}

export function moveBlock(tree: Block[], id: string, direction: -1 | 1): Block[] {
  const out = (list: Block[]): Block[] => {
    const idx = list.findIndex((b) => b.id === id);
    if (idx >= 0) {
      const next = idx + direction;
      if (next < 0 || next >= list.length) return list;
      const arr = [...list];
      const [item] = arr.splice(idx, 1);
      arr.splice(next, 0, item);
      return arr;
    }
    return list.map((b) => (b.children ? { ...b, children: out(b.children) } : b));
  };
  return out(tree);
}

export function duplicateBlock(tree: Block[], id: string): Block[] {
  const cloneIds = (b: Block): Block => ({
    ...b,
    id: newId(),
    props: { ...b.props },
    children: b.children?.map(cloneIds),
  });
  const out = (list: Block[]): Block[] => {
    const idx = list.findIndex((b) => b.id === id);
    if (idx >= 0) {
      const arr = [...list];
      arr.splice(idx + 1, 0, cloneIds(arr[idx]));
      return arr;
    }
    return list.map((b) => (b.children ? { ...b, children: out(b.children) } : b));
  };
  return out(tree);
}
